// src/types/assignment.types.ts
import { Timestamp } from 'firebase/firestore';

// ============================================
// ASSIGNMENT ACTIONS
// ============================================

export type AssignmentAction =
  | 'created'
  | 'assigned'
  | 'unassigned'
  | 'reassigned'
  | 'status_changed'
  | 'survey_linked'
  | 'survey_unlinked'
  | 'addresses_added'
  | 'addresses_removed'
  | 'addresses_imported'
  | 'territory_updated'
  | 'batch_update'
  | 'archived'
  | 'restored'
  | 'deleted';

// ============================================
// WORKLOAD ASSIGNMENT LOG (W-A-L)
// ============================================

export interface WorkloadAssignmentLog {
  id: string;
  workloadId: string;
  workloadName: string;
  projectId: string;

  // Action details
  action: AssignmentAction;
  description: string;

  // Who made the change
  performedBy: string;
  performedByName: string;
  performedAt: Timestamp;

  // Change details
  changes: AssignmentChange[];

  // For batch operations
  batchId?: string;
  batchSize?: number;

  // Additional context
  metadata?: Record<string, any>;
}

export interface AssignmentChange {
  field: string;
  previousValue: any;
  newValue: any;
  displayPrevious?: string; // Human-readable previous value
  displayNew?: string;      // Human-readable new value
}

// ============================================
// DTOs / INPUT TYPES
// ============================================

export interface CreateAssignmentLogDto {
  workloadId: string;
  workloadName: string;
  projectId: string;
  action: AssignmentAction;
  description: string;
  changes: AssignmentChange[];
  batchId?: string;
  batchSize?: number;
  metadata?: Record<string, any>;
}

export interface AssignmentLogFilters {
  workloadId?: string;
  projectId?: string;
  action?: AssignmentAction[];
  performedBy?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  batchId?: string;
}

// ============================================
// BATCH OPERATIONS
// ============================================

export interface BatchOperation {
  id: string;
  projectId: string;
  operationType: 'assign' | 'unassign' | 'status_change' | 'delete' | 'archive';
  workloadIds: string[];
  targetUserIds?: string[];
  targetGroupIds?: string[];
  targetStatus?: string;
  performedBy: string;
  performedByName: string;
  performedAt: Timestamp;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'partial';
  successCount: number;
  failCount: number;
  errors?: BatchOperationError[];
}

export interface BatchOperationError {
  workloadId: string;
  workloadName: string;
  error: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getActionLabel = (action: AssignmentAction): string => {
  const labels: Record<AssignmentAction, string> = {
    created: 'Created',
    assigned: 'Assigned',
    unassigned: 'Unassigned',
    reassigned: 'Reassigned',
    status_changed: 'Status Changed',
    survey_linked: 'Survey Linked',
    survey_unlinked: 'Survey Unlinked',
    addresses_added: 'Addresses Added',
    addresses_removed: 'Addresses Removed',
    addresses_imported: 'Addresses Imported',
    territory_updated: 'Territory Updated',
    batch_update: 'Batch Update',
    archived: 'Archived',
    restored: 'Restored',
    deleted: 'Deleted',
  };
  return labels[action];
};

export const getActionIcon = (action: AssignmentAction): string => {
  const icons: Record<AssignmentAction, string> = {
    created: 'Plus',
    assigned: 'UserPlus',
    unassigned: 'UserMinus',
    reassigned: 'RefreshCw',
    status_changed: 'Activity',
    survey_linked: 'Link',
    survey_unlinked: 'Unlink',
    addresses_added: 'MapPin',
    addresses_removed: 'MapPinOff',
    addresses_imported: 'Upload',
    territory_updated: 'Map',
    batch_update: 'Layers',
    archived: 'Archive',
    restored: 'RotateCcw',
    deleted: 'Trash2',
  };
  return icons[action];
};

export const getActionColor = (action: AssignmentAction): string => {
  const colors: Record<AssignmentAction, string> = {
    created: '#10B981',      // Green
    assigned: '#3B82F6',     // Blue
    unassigned: '#F59E0B',   // Amber
    reassigned: '#8B5CF6',   // Purple
    status_changed: '#6366F1', // Indigo
    survey_linked: '#14B8A6', // Teal
    survey_unlinked: '#F97316', // Orange
    addresses_added: '#22C55E', // Green
    addresses_removed: '#EF4444', // Red
    addresses_imported: '#0EA5E9', // Sky
    territory_updated: '#A855F7', // Purple
    batch_update: '#6366F1',  // Indigo
    archived: '#64748B',     // Slate
    restored: '#22C55E',     // Green
    deleted: '#EF4444',      // Red
  };
  return colors[action];
};

// Generate description for common actions
export const generateActionDescription = (
  action: AssignmentAction,
  changes: AssignmentChange[],
  metadata?: Record<string, any>
): string => {
  switch (action) {
    case 'created':
      return 'Workload was created';
    case 'assigned':
      const assignedUsers = changes.find(c => c.field === 'assignedTo')?.displayNew;
      return assignedUsers ? `Assigned to ${assignedUsers}` : 'Users were assigned';
    case 'unassigned':
      const unassignedUsers = changes.find(c => c.field === 'assignedTo')?.displayPrevious;
      return unassignedUsers ? `Unassigned from ${unassignedUsers}` : 'Users were unassigned';
    case 'reassigned':
      const from = changes.find(c => c.field === 'assignedTo')?.displayPrevious;
      const to = changes.find(c => c.field === 'assignedTo')?.displayNew;
      return `Reassigned from ${from || 'previous users'} to ${to || 'new users'}`;
    case 'status_changed':
      const statusChange = changes.find(c => c.field === 'status');
      return `Status changed from ${statusChange?.displayPrevious || 'unknown'} to ${statusChange?.displayNew || 'unknown'}`;
    case 'survey_linked':
      return `Survey "${metadata?.surveyName || 'Unknown'}" was linked`;
    case 'survey_unlinked':
      return `Survey "${metadata?.surveyName || 'Unknown'}" was unlinked`;
    case 'addresses_added':
      const addedCount = metadata?.count || changes.find(c => c.field === 'addressCount')?.newValue;
      return `${addedCount || 'Some'} addresses were added`;
    case 'addresses_removed':
      const removedCount = metadata?.count || changes.find(c => c.field === 'addressCount')?.previousValue;
      return `${removedCount || 'Some'} addresses were removed`;
    case 'addresses_imported':
      return `Addresses imported from ${metadata?.source || 'file'}`;
    case 'territory_updated':
      return 'Territory boundaries were updated';
    case 'batch_update':
      return `Batch update applied to ${metadata?.workloadCount || 'multiple'} workloads`;
    case 'archived':
      return 'Workload was archived';
    case 'restored':
      return 'Workload was restored from archive';
    case 'deleted':
      return 'Workload was deleted';
    default:
      return 'Workload was modified';
  }
};
